/**
 * How a litter's photographings are READ.
 *
 * `measurements.ts` is the same file for weighings, and this one is deliberately
 * much smaller: a weighing has to be pivoted from one document per session into
 * one line per animal, because the growth arithmetic asks about one puppy. A
 * photographing is read as what it is — a day, and who was in it — so all this
 * file owns for now is the order the archive is listed in.
 *
 * Pure, and no SDK: `platform/` is compiled into the browser bundle and inlined
 * into the functions bundle.
 */
import type { PhotoSession, PhotoSubject } from './types/index.ts';

/**
 * The log's order: the most recent photographing first.
 *
 * The same shape as `byNewest` for weighings, and a separate function rather
 * than a shared generic because the two order by different fields for different
 * reasons: a weighing is a moment (`recordedAt`), a photographing is a day
 * (`takenAt`). Ties are broken by `createdAt` — two sessions dated to the same
 * day are ordered by which was actually uploaded first, so the list does not
 * reshuffle itself between renders.
 */
export const byNewestSession = (a: PhotoSession, b: PhotoSession): number =>
  b.takenAt - a.takenAt || b.createdAt - a.createdAt;


/**
 * How many photographs one session holds — the litter's own and the puppies'
 * together.
 *
 * Counted rather than stored: the paths are the only record of what is in a
 * session, and a denormalised total is a number that goes wrong the first time a
 * correction adds one. It is what the deletion question is asked in — "12
 * photographs from the 3rd will be gone" is a number worth reading before saying
 * yes.
 */
export const photoCount = (session: Pick<PhotoSession, 'paths' | 'subjects'>): number =>
  session.paths.length +
  session.subjects.reduce((count, subject) => count + subject.paths.length, 0);

/**
 * One animal's frames from one photographing, or none.
 *
 * The reading `PhotoSession` asks for, in one place rather than a `find` per
 * caller: a session names its subjects by id, a subject the litter has since
 * lost is dropped rather than failed on, and an animal that was not in front of
 * the camera that afternoon has no entry at all — which is the same answer as an
 * entry with no paths in it, and callers should not have to tell those apart.
 *
 * It is also what makes one animal's own line drawable: `subjectPaths` is the
 * filter that turns a litter's archive into this puppy's, with the litter's own
 * group shots left out because they are of nobody in particular.
 */
export const subjectPaths = (
  session: Pick<PhotoSession, 'subjects'>,
  offspringId: string,
): readonly string[] =>
  session.subjects.find((subject) => subject.offspringId === offspringId)?.paths ?? [];

/**
 * The photographing already standing on a day, or none.
 *
 * ONE SESSION PER DAY IS THE RULE, and this is the question that keeps it. A
 * breeder photographs the litter in the morning, comes back at four with the
 * ones they missed, and taps Add photos again: two documents dated to the same
 * midnight, two cards on the timeline saying "Day 12", and the afternoon's
 * pictures of one puppy in a different pile from the morning's. Nothing about
 * the archive is read finer than a day — so a day is one record, and a second
 * upload for it is folded into the first rather than filed beside it. See
 * `mergePhotos` and `savePhotoSession` in app/src/api/mutations.ts.
 *
 * THE ONE ALREADY THERE SURVIVES, WHICH IS WHY THIS RETURNS THE EARLIEST. It
 * keeps `recordedBy` and `createdAt` — who first photographed that day, and
 * when — and the merger signs `updatedBy`, exactly as any other correction
 * does. Picking the newest instead would make every afternoon's second upload
 * rewrite the morning's attribution.
 *
 * `except` is the session being corrected: a wizard saving changes to a
 * document must not find that document and merge it into itself. A day that has
 * somehow ended up with two records — data written before this rule existed —
 * yields the older, and migration 003 collapses the rest.
 *
 * `takenAt` is compared exactly, not by calendar arithmetic: every session is
 * written as local midnight of its day (see `PhotoSession` in types/index.ts),
 * so the stored number *is* the day, and a comparison that re-derived one would
 * be a second opinion about which day a session is on.
 */
export const sessionOnDay = (
  sessions: readonly PhotoSession[],
  takenAt: number,
  except?: string | undefined,
): PhotoSession | null =>
  sessions.reduce<PhotoSession | null>(
    (earliest, session) =>
      session.takenAt !== takenAt || session.id === except
        ? earliest
        : earliest === null || session.createdAt < earliest.createdAt
          ? session
          : earliest,
    null,
  );

/**
 * Several records of one day's photographs, as the one record it should have
 * been.
 *
 * IN THE ORDER GIVEN, oldest record first, because that is the order the
 * pictures were taken in and the order the timeline reads them in: the morning's
 * group shots, then the afternoon's. Within a puppy, the same — its frames from
 * each record, one after the other, under a single subject entry, which is what
 * makes one pile per puppy per day rather than two.
 *
 * A PUPPY'S ENTRY APPEARS WHERE IT FIRST DID. The subjects array is not a
 * board and carries no meaning in its own order — the timeline maps over the
 * litter's offspring and looks each one up (see `SessionPiles`) — but a stable
 * order means a merge that changes nothing writes a document identical to the
 * one it replaced.
 *
 * PATHS ARE DEDUPLICATED. The same file cannot honestly be in a day twice, and
 * a merge run over something that has already been merged must not double it.
 *
 * Pure, and takes the two halves of a session rather than whole documents, so
 * the wizard can merge what is on its screen — which is not a document yet —
 * into what is already saved.
 */
export type PhotoContent = {
  readonly paths: readonly string[];
  readonly subjects: readonly PhotoSubject[];
};

export const mergePhotos = (
  records: readonly PhotoContent[],
): Pick<PhotoSession, 'paths' | 'subjects'> => {
  const byOffspring = new Map<string, string[]>();

  for (const record of records) {
    for (const subject of record.subjects) {
      const gathered = byOffspring.get(subject.offspringId);
      if (gathered === undefined) byOffspring.set(subject.offspringId, [...subject.paths]);
      else gathered.push(...subject.paths);
    }
  }

  return {
    paths: unique(records.flatMap((record) => record.paths)),
    subjects: [...byOffspring].map(([offspringId, paths]) => ({
      offspringId,
      paths: unique(paths),
    })),
  };
};

/** First occurrence wins, order kept. */
const unique = (paths: readonly string[]): string[] => [...new Set(paths)];
